<template>
  <div class="contract-list">
    <div class="page-header">
      <h2>계약서 관리</h2>
      <el-button @click="helpVisible = true">
        <el-icon><QuestionFilled /></el-icon>
        도움말
      </el-button>
    </div>

    <!-- 탭 -->
    <el-tabs v-model="activeTab" @tab-change="handleTabChange">
      <!-- ===== 계약서 목록 탭 ===== -->
      <el-tab-pane label="계약서" name="contracts">
        <!-- 전체 흐름 안내: 누가 → 무엇을 → 다음 -->
        <el-card class="flow-card">
          <ContractFlowSteps mode="overview" />
        </el-card>

        <div class="tab-actions">
          <el-button type="primary" @click="openCreateDialog">
            <el-icon><Plus /></el-icon>
            계약서 생성
          </el-button>
        </div>

        <!-- 필터 -->
        <el-card class="filter-card">
          <el-form :inline="true" :model="filters">
            <el-form-item label="상태">
              <el-select v-model="filters.status" clearable placeholder="전체" style="width:160px">
                <el-option label="작성중" value="DRAFT" />
                <el-option label="배포됨" value="DISTRIBUTED" />
                <el-option label="진행중" value="IN_PROGRESS" />
                <el-option label="완료" value="COMPLETED" />
                <el-option label="기한만료" value="EXPIRED" />
                <el-option label="취소" value="CANCELLED" />
              </el-select>
            </el-form-item>
            <el-form-item label="분류">
              <el-select v-model="filters.template__category" clearable placeholder="전체" style="width:180px">
                <el-option v-for="c in CATEGORY_OPTIONS" :key="c.value" :label="c.label" :value="c.value" />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="fetchContracts">검색</el-button>
              <el-button @click="resetFilters">초기화</el-button>
            </el-form-item>
          </el-form>
        </el-card>

        <!-- 테이블 -->
        <el-card>
          <el-table :data="contractStore.contracts" v-loading="contractStore.contractLoading" @row-click="goDetail" style="cursor:pointer">
            <el-table-column prop="contract_number" label="계약번호" width="160" />
            <el-table-column prop="title" label="계약서명" min-width="200" />
            <el-table-column prop="template_category_display" label="분류" width="120" />
            <el-table-column prop="status_display" label="상태" width="100">
              <template #default="{ row }">
                <el-tag :type="statusTagType(row.status)" size="small">{{ row.status_display }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="진행률" width="180">
              <template #default="{ row }">
                <div style="display:flex;align-items:center;gap:8px;">
                  <el-progress :percentage="row.consent_rate" :stroke-width="6" style="flex:1" />
                  <span style="font-size:12px;color:#666;">{{ row.consented_count }}/{{ row.total_parties }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="deadline" label="기한" width="120">
              <template #default="{ row }">
                <span :style="{ color: row.is_overdue ? '#F56C6C' : '' }">
                  {{ formatDate(row.deadline) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="created_by_name" label="작성자" width="100" />
            <el-table-column prop="created_at" label="작성일" width="110">
              <template #default="{ row }">{{ formatDate(row.created_at) }}</template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-tab-pane>

      <!-- ===== 양식 관리 탭 ===== -->
      <el-tab-pane label="양식 관리" name="templates">
        <el-alert type="info" :closable="false" show-icon class="flow-card">
          <template #title>양식 = 계약서 종류별로 한 번만 만드는 "틀"</template>
          같은 종류의 계약서를 배포할 때마다 다시 정할 필요가 없는 것들을 여기에 둡니다 —
          <strong>직원이 체크할 동의 문구</strong>, <strong>끝까지 읽어야 하는지·몇 초 이상 읽어야 하는지</strong>(법적 증거 요건),
          그리고 <strong>빈 서식 파일</strong>(선택). 실제로 직원이 읽을 계약서 파일과 대상자·기한은
          <strong>계약서 탭에서 건별로</strong> 정합니다. 보통 분류당 양식 1개면 충분합니다.
        </el-alert>

        <div class="tab-actions">
          <el-button type="primary" @click="openTemplateDialog()">
            <el-icon><Plus /></el-icon>
            양식 등록
          </el-button>
        </div>

        <el-card>
          <el-table :data="contractStore.templates" v-loading="templatesLoading" empty-text="아직 양식이 없습니다. [양식 등록]으로 첫 양식을 만드세요 — 분류를 고르면 동의 문구와 열람 요건이 기본값으로 채워집니다.">
            <el-table-column prop="name" label="양식명" min-width="200" />
            <el-table-column prop="category_display" label="분류" width="140" />
            <el-table-column prop="version" label="버전" width="80" align="center" />
            <el-table-column label="양식파일" width="100" align="center">
              <template #default="{ row }">
                <el-link v-if="row.template_file" :href="row.template_file" target="_blank" type="primary">내려받기</el-link>
                <span v-else style="color:var(--el-text-color-placeholder)">-</span>
              </template>
            </el-table-column>
            <el-table-column label="조건/자동생성" width="110" align="center">
              <template #default="{ row }">
                <span>{{ (row.term_fields || []).length }}항목</span>
                <el-tag v-if="row.has_body" type="success" size="small" style="margin-left:4px">자동</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="동의항목" width="100" align="center">
              <template #default="{ row }">
                {{ (row.consent_items || []).length }}개
              </template>
            </el-table-column>
            <el-table-column label="전문열람" width="100" align="center">
              <template #default="{ row }">
                <el-tag :type="row.require_full_read ? 'danger' : 'info'" size="small">
                  {{ row.require_full_read ? '필수' : '선택' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="최소열람(초)" width="110" align="center">
              <template #default="{ row }">
                {{ row.minimum_read_seconds || '-' }}
              </template>
            </el-table-column>
            <el-table-column label="상태" width="80" align="center">
              <template #default="{ row }">
                <el-tag :type="row.is_active ? 'success' : 'info'" size="small">
                  {{ row.is_active ? '활성' : '비활성' }}
                </el-tag>
                <el-tooltip v-if="row.locked" content="배포된 계약서가 있어 내용을 고칠 수 없습니다. 고치려면 [새 버전]" placement="top">
                  <el-tag type="warning" size="small" style="margin-left:4px">잠김</el-tag>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column label="작업" width="250" align="center">
              <template #default="{ row }">
                <el-button size="small" @click="openTemplateDialog(row)">{{ row.locked ? '보기' : '수정' }}</el-button>
                <el-button v-if="row.locked && row.is_active" size="small" type="warning" plain @click="handleNewVersion(row)">새 버전</el-button>
                <el-tooltip :content="row.is_active ? '계약서 생성 창의 양식 목록에서 뺍니다 (기존 계약서 영향 없음)' : '다시 계약서 생성에 쓸 수 있게 합니다'" placement="top">
                  <el-button size="small" :type="row.is_active ? 'info' : 'success'" plain @click="toggleTemplateActive(row)">{{ row.is_active ? '비활성' : '활성' }}</el-button>
                </el-tooltip>
                <el-button v-if="!row.contract_count" size="small" type="danger" @click="handleDeleteTemplate(row)">삭제</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-tab-pane>
    </el-tabs>

    <!-- ===== 계약서 생성 다이얼로그 ===== -->
    <el-dialog v-model="createDialogVisible" title="계약서 생성" width="600px">
      <el-form :model="createForm" label-width="120px">
        <el-form-item label="양식" required>
          <el-select v-model="createForm.template" placeholder="양식 선택" style="width:100%" @change="handleTemplateChange">
            <el-option
              v-for="t in contractStore.templates.filter((x) => x.is_active)"
              :key="t.id"
              :label="`${t.name} (${t.category_display})`"
              :value="t.id"
            />
          </el-select>
          <div v-if="selectedTemplateFile" class="form-hint">
            원본 서식: <el-link :href="selectedTemplateFile" target="_blank" type="primary">내려받기</el-link>
            — 이 서식으로 문서를 작성해 아래(공통) 또는 상세 화면(개별)에서 올립니다.
          </div>
        </el-form-item>
        <el-form-item label="계약서명" required>
          <el-input v-model="createForm.title" placeholder="예: 2026년 근로계약서" />
        </el-form-item>
        <!-- 자동 생성 양식: 배포유형·파일 선택 없음 — 시스템이 직원별 문서를 만든다 -->
        <el-alert v-if="selectedTemplateAuto" type="success" :closable="false" show-icon style="margin-bottom:16px">
          <template #title>이 양식은 계약서를 자동으로 만듭니다 — 대상과 기한만 정하세요</template>
          {{ selectedTemplateUsesMaster
            ? '근로조건 관리에 등록된 각 직원의 조건(급여 구성·고정연장시간)과 직원 정보(주소·연락처·입사일)로 직원별 계약서 PDF가 생성됩니다. 아래 계약 시작일 기준으로 적용 중인 조건을 가져오며, 조건이 없는 직원은 생성 후 상세 화면에서 알려 줍니다.'
            : '양식의 본문 서식으로 직원별 문서가 생성됩니다(성명·소속·회사명 자동 기입).' }}
        </el-alert>
        <el-form-item v-if="selectedTemplateUsesMaster" label="계약 시작일" required>
          <el-date-picker v-model="createForm.contract_start_date" type="date" placeholder="예: 2026-01-01" value-format="YYYY-MM-DD" style="width:100%" />
          <div class="form-hint">계약서 제1조의 계약 시작일이자 <strong>근로조건 기준일</strong>입니다. 근로조건 관리에서 이 날짜에 적용 중인 조건(적용일이 이 날짜 이전인 것 중 최신)을 가져옵니다. 2026년 계약이면 2026-01-01, 내년 계약을 미리 만들면 2027-01-01.</div>
        </el-form-item>
        <el-form-item v-if="!selectedTemplateAuto" label="배포 유형" required>
          <el-radio-group v-model="createForm.distribute_type">
            <el-radio label="COMMON">공통 (전원 동일 문서)</el-radio>
            <el-radio label="INDIVIDUAL">개별 (직원별 다른 문서)</el-radio>
          </el-radio-group>
          <div v-if="distributeTypeHint" class="form-hint">{{ distributeTypeHint }}</div>
        </el-form-item>
        <el-form-item v-if="!selectedTemplateAuto && createForm.distribute_type === 'COMMON'" label="계약서 파일" required>
          <el-upload
            :auto-upload="false"
            :limit="1"
            accept=".pdf,.docx"
            :on-change="handleFileChange"
          >
            <el-button>파일 선택</el-button>
            <template #tip>
              <div class="el-upload__tip">전 직원에게 동일하게 배포할 계약서 파일</div>
            </template>
          </el-upload>
        </el-form-item>
        <el-alert
          v-if="!selectedTemplateAuto && createForm.distribute_type === 'INDIVIDUAL'"
          type="info"
          :closable="false"
          show-icon
          style="margin-bottom:16px"
        >
          개별 배포 유형입니다. 계약서 생성 후 상세 화면에서 직원별 파일을 업로드해주세요.
        </el-alert>
        <el-form-item label="확인 기한" required>
          <el-date-picker
            v-model="createForm.deadline"
            type="datetime"
            placeholder="기한 선택"
            format="YYYY-MM-DD HH:mm"
            value-format="YYYY-MM-DDTHH:mm:ss"
            style="width:100%"
          />
        </el-form-item>
        <el-form-item label="독촉 알림">
          <el-select v-model="createForm.reminder_days" multiple placeholder="기한 N일 전" style="width:100%">
            <el-option :label="1" :value="1" />
            <el-option :label="3" :value="3" />
            <el-option :label="5" :value="5" />
            <el-option :label="7" :value="7" />
            <el-option :label="14" :value="14" />
          </el-select>
        </el-form-item>
        <el-form-item label="대상 선택" required>
          <el-radio-group v-model="createForm.target_type" @change="handleTargetTypeChange">
            <el-radio label="ALL">전직원</el-radio>
            <el-radio label="DEPARTMENT">부서별</el-radio>
            <el-radio label="INDIVIDUAL">개별 선택</el-radio>
          </el-radio-group>
        </el-form-item>
        <!-- 부서 선택 -->
        <el-form-item v-if="createForm.target_type === 'DEPARTMENT'" label="부서">
          <el-select
            v-model="createForm.target_departments"
            multiple
            placeholder="부서를 선택하세요"
            style="width:100%"
            :loading="deptLoading"
          >
            <el-option
              v-for="dept in departments"
              :key="dept.id"
              :label="dept.name"
              :value="dept.id"
            />
          </el-select>
        </el-form-item>
        <!-- 개별 직원 선택 -->
        <el-form-item v-if="createForm.target_type === 'INDIVIDUAL'" label="직원">
          <div style="width:100%">
            <div style="display:flex;gap:8px;margin-bottom:8px;">
              <el-select
                v-model="empDeptFilter"
                clearable
                placeholder="부서 필터"
                style="width:180px"
                @change="searchEmployees"
              >
                <el-option
                  v-for="dept in departments"
                  :key="dept.id"
                  :label="dept.name"
                  :value="dept.id"
                />
              </el-select>
              <el-input
                v-model="empSearchKeyword"
                placeholder="이름/사번 검색"
                clearable
                style="flex:1"
                @input="debounceSearchEmployees"
              />
            </div>
            <el-table
              :data="employeeList"
              size="small"
              max-height="240"
              @selection-change="handleEmployeeSelection"
              ref="empTableRef"
              v-loading="empLoading"
            >
              <el-table-column type="selection" width="40" />
              <el-table-column prop="name" label="이름" width="100" />
              <el-table-column prop="department" label="부서" width="120" />
              <el-table-column prop="username" label="사번" />
            </el-table>
            <div v-if="selectedEmployees.length > 0" style="margin-top:8px;font-size:13px;color:#409EFF;">
              {{ selectedEmployees.length }}명 선택됨
            </div>
          </div>
        </el-form-item>
        <el-form-item label="메모">
          <el-input v-model="createForm.memo" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createDialogVisible = false">취소</el-button>
        <el-button type="primary" @click="submitCreate" :loading="creating">생성</el-button>
      </template>
    </el-dialog>

    <!-- ===== 양식 등록/수정 다이얼로그 ===== -->
    <el-dialog v-model="templateDialogVisible" :title="editingTemplate ? (isLockedTemplate ? '양식 보기 (잠김)' : '양식 수정') : '양식 등록'" width="760px">
      <el-alert v-if="isLockedTemplate" type="warning" :closable="false" show-icon style="margin-bottom:12px">
        <template #title>이 양식(v{{ editingTemplate.version }})으로 배포된 계약서가 {{ editingTemplate.contract_count }}건 있어 내용을 고칠 수 없습니다.</template>
        직원이 읽고 동의한 문서와 서식이 달라지면 안 되기 때문입니다. 문구·조건·동의 규칙을 바꾸려면
        <el-button size="small" type="warning" plain @click="handleNewVersion(editingTemplate)" style="margin:0 4px">새 버전 만들기</el-button>
        — 내용이 복사된 v{{ (editingTemplate.version || 1) + 1 }}이 만들어지고 이 버전은 비활성됩니다(기존 계약서는 이 버전 그대로 유지). 이름·설명·사용 여부는 여기서 바꿀 수 있습니다.
      </el-alert>
      <el-form :model="templateForm" label-width="130px" :disabled="isLockedTemplate">

        <!-- ① 기본 정보 -->
        <h5 class="tpl-section">① 기본 정보</h5>
        <el-form-item label="분류" required>
          <el-select v-model="templateForm.category" placeholder="분류 선택" style="width:100%" @change="applyCategoryPreset">
            <el-option v-for="c in CATEGORY_OPTIONS" :key="c.value" :label="c.label" :value="c.value" />
          </el-select>
          <div class="form-hint">분류를 고르면 아래 동의 문구·열람 요건이 그 종류에 맞는 기본값으로 채워집니다. 계약서 생성 시 배포 방식(개별/공통)의 기본값도 분류로 정해집니다.</div>
        </el-form-item>
        <el-form-item label="양식명" required>
          <el-input v-model="templateForm.name" placeholder="예: 표준근로계약서" />
          <div class="form-hint">계약서 생성 창의 양식 목록에 이 이름으로 나옵니다. 연도는 양식명이 아니라 계약서명에 붙이세요(양식은 여러 해 재사용).</div>
        </el-form-item>
        <el-form-item label="설명">
          <el-input v-model="templateForm.description" type="textarea" :rows="2" placeholder="예: 정규직 입사자용. 2026-03 법무 검토본." />
          <div class="form-hint">담당자끼리 보는 메모입니다. 직원에게는 보이지 않습니다.</div>
        </el-form-item>

        <!-- ② 직원이 체크할 동의 문구 -->
        <h5 class="tpl-section">② 직원이 체크할 동의 문구</h5>
        <el-form-item label="동의 항목">
          <div class="consent-items-area">
            <div v-for="(item, idx) in templateForm.consent_items" :key="idx" class="consent-item-row">
              <el-input v-model="templateForm.consent_items[idx]" placeholder="예: 위 근로계약 내용을 확인하였습니다" style="flex:1" />
              <el-checkbox v-model="templateForm.consent_items_required[idx]" style="margin-left:8px">필수</el-checkbox>
              <el-button type="danger" :icon="Delete" circle size="small" @click="removeConsentItem(idx)" style="margin-left:4px" />
            </div>
            <el-button type="primary" link @click="addConsentItem">+ 항목 추가</el-button>
            <div class="form-hint">"필수"를 켜면 그 항목을 체크해야 동의 버튼이 눌립니다. 선택 항목은 체크하지 않아도 동의할 수 있습니다.</div>
          </div>
        </el-form-item>
        <el-form-item label="직원 화면 미리보기">
          <div class="consent-preview">
            <div class="consent-preview-title">동의</div>
            <div v-for="(item, idx) in previewItems" :key="idx" class="consent-preview-row">
              <el-checkbox disabled>{{ item.label }}</el-checkbox>
              <el-tag v-if="item.required" type="danger" size="small" style="margin-left:4px">필수</el-tag>
            </div>
            <div v-if="previewItems.length === 0" class="form-hint">동의 항목을 하나 이상 입력하세요.</div>
            <div class="consent-preview-actions">
              <el-button type="primary" disabled>동의합니다</el-button>
              <el-button disabled>거부</el-button>
            </div>
          </div>
        </el-form-item>

        <!-- ③ 열람 요건 (법적 증거) -->
        <h5 class="tpl-section">③ 열람·서명 요건 — "읽지 않고 동의했다"는 주장을 막는 장치</h5>
        <el-form-item label="직원 서명 필수">
          <el-switch v-model="templateForm.require_signature" />
          <div class="form-hint">켜면 동의할 때 화면에 자필 서명을 받고, 회사 직인·직원 서명·동의 기록이 담긴 <strong>완성본 PDF</strong>가 자동 생성되어 직원에게 교부됩니다. 근로계약서·서약서 모두 켜두세요. 회사 직인은 기준정보 &gt; 회사 정보의 직인 이미지를 씁니다.</div>
        </el-form-item>
        <el-form-item label="끝까지 읽기 필수">
          <el-switch v-model="templateForm.require_full_read" />
          <div class="form-hint">켜면 직원이 문서를 끝까지 보고 [전체 확인 완료]를 누른 뒤에만 동의할 수 있습니다. 두 문서 모두 켜두기를 권합니다.</div>
        </el-form-item>
        <el-form-item label="최소 열람시간">
          <el-input-number v-model="templateForm.minimum_read_seconds" :min="0" :max="600" :step="10" />
          <span class="form-inline-hint">초</span>
          <div class="form-hint">문서를 연 뒤 이 시간이 지나야 동의 버튼이 열립니다. 0이면 제한 없음. 한 장짜리 서약서는 30초, 여러 장 계약서는 60~120초 정도가 적당합니다.</div>
        </el-form-item>

        <!-- ⑤ 계약 조건 항목 -->
        <h5 class="tpl-section">⑤ 계약 조건 항목 — 직원마다 값이 다른 것 (계약기간·연봉 등)</h5>
        <el-form-item label="조건 항목">
          <div class="consent-items-area">
            <div v-for="(f, idx) in templateForm.term_fields" :key="idx" class="term-row">
              <el-input v-model="f.label" placeholder="항목명 (예: 연봉)" style="width:170px" />
              <el-select v-model="f.type" style="width:110px">
                <el-option label="텍스트" value="text" />
                <el-option label="날짜" value="date" />
                <el-option label="금액" value="money" />
                <el-option label="숫자" value="number" />
                <el-option label="선택" value="select" />
              </el-select>
              <el-input v-if="f.type === 'select'" v-model="f.optionsText" placeholder="선택지 (쉼표 구분)" style="width:180px" />
              <el-input v-else-if="f.type === 'text'" v-model="f.default" placeholder="기본값 (선택)" style="width:180px" />
              <el-checkbox v-model="f.required">필수</el-checkbox>
              <el-button type="danger" :icon="Delete" circle size="small" @click="templateForm.term_fields.splice(idx, 1)" />
            </div>
            <el-button type="primary" link @click="templateForm.term_fields.push({ label: '', type: 'text', required: true })">+ 항목 추가</el-button>
            <div class="form-hint">
              계약서를 만든 뒤 대상자마다 이 항목의 값을 입력합니다(엑셀 일괄 가능). 값은 직원 동의 화면에 "계약 조건"으로 표시되고
              동의 기록에 남으며, 아래 본문 서식이 있으면 문서에 자동으로 채워집니다. 항목이 없으면 이 기능을 쓰지 않는 것입니다.
            </div>
          </div>
        </el-form-item>

        <!-- ⑥ 본문 서식 (자동 생성) -->
        <h5 class="tpl-section">⑥ 본문 서식 — 채우면 담당자가 PDF를 만들지 않아도 됩니다</h5>
        <el-form-item label="본문 서식(HTML)">
          <div class="consent-items-area">
            <div class="term-row" style="margin-bottom:6px">
              <el-button size="small" @click="loadStandardBody" :disabled="!hasStandardBody">표준 서식 불러오기 ({{ categoryLabel }})</el-button>
              <el-button size="small" :disabled="!templateForm.body_html" :loading="previewingBody" @click="previewBody">미리보기 (PDF)</el-button>
              <el-button size="small" type="danger" plain :disabled="!templateForm.body_html" @click="templateForm.body_html = ''">서식 비우기</el-button>
            </div>
            <el-input v-model="templateForm.body_html" type="textarea" :rows="8" placeholder="비워 두면 담당자가 직원별 PDF를 직접 올립니다. 표준 서식을 불러온 뒤 문구를 고쳐 쓰세요." style="font-family:monospace;font-size:12px" />
            <div class="form-hint">
              서식이 있으면 계약 조건을 입력하는 순간 직원별 계약서 PDF가 자동 생성되어 개별파일로 붙습니다.
              문구 안의 <code v-pre>{{ terms.항목키 }}</code>, <code v-pre>{{ employee.name }}</code>, <code v-pre>{{ company.name }}</code> 자리에 값이 들어갑니다.
              서식 수정이 어려우면 문구만 바꾸고 <code v-pre>{{ ... }}</code>는 그대로 두세요. 항목키: {{ termKeysText || '(조건 항목을 먼저 추가)' }}
            </div>
          </div>
        </el-form-item>

        <el-form-item v-if="!templateForm.body_html" label="빈 서식 파일 (수동)">
          <el-upload
            :auto-upload="false"
            :limit="1"
            :show-file-list="true"
            accept=".pdf,.docx,.doc,.hwp,.hwpx"
            :on-change="(file) => (templateFile = file.raw)"
            :on-remove="() => (templateFile = null)"
          >
            <el-button>{{ editingTemplate?.template_file ? '파일 교체' : '파일 선택' }}</el-button>
          </el-upload>
          <div class="form-hint">
            <template v-if="editingTemplate?.template_file">
              현재 파일: <el-link :href="editingTemplate.template_file" target="_blank" type="primary">내려받기</el-link> ·
            </template>
            본문 서식을 쓰지 않고 담당자가 직원별 PDF를 직접 만들어 올릴 때만 필요합니다 — 내려받아 채우는 원본(Word/PDF/HWP). 위 본문 서식을 넣으면 이 칸은 사라집니다.
          </div>
        </el-form-item>

      </el-form>
      <!-- ④ 관리 — 잠긴 양식도 이름·설명·사용 여부는 바꿀 수 있으므로 별도 폼 -->
      <el-form :model="templateForm" label-width="130px">
        <h5 class="tpl-section">④ 관리</h5>
        <el-form-item label="버전">
          <span>v{{ templateForm.version || 1 }}</span>
          <div class="form-hint">배포된 뒤에는 내용을 고칠 수 없고 [새 버전 만들기]로 다음 판이 생깁니다. 어떤 직원이 몇 번째 판에 동의했는지 완성본에 남습니다.</div>
        </el-form-item>
        <el-form-item label="사용 중">
          <el-switch v-model="templateForm.is_active" />
          <div class="form-hint">끄면 계약서 생성 창의 양식 목록에서 빠집니다(이미 배포된 계약서에는 영향 없음). 삭제 대신 이걸 쓰세요.</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="templateDialogVisible = false">취소</el-button>
        <el-button type="primary" @click="submitTemplate" :loading="savingTemplate">
          {{ editingTemplate ? '수정' : '등록' }}
        </el-button>
      </template>
    </el-dialog>

    <!-- 도움말 패널 -->
    <HelpPanel v-model="helpVisible" :content-path="'screens/contract-list'" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";
import { Plus, Delete, QuestionFilled } from "@element-plus/icons-vue";
import { useContractStore } from "@/stores/contractStore";
import HelpPanel from "@/components/help/HelpPanel.vue";
import ContractFlowSteps from "@/components/contract/ContractFlowSteps.vue";

const helpVisible = ref(false);

// 분류별 기본 배포유형 — 양식 선택 시 자동 채움 (필요하면 바꿀 수 있음)
// 근로/연봉계약서는 사람마다 조건이 달라 개별, 서약서·동의서류는 전원 동일 문서라 공통
const DEFAULT_DISTRIBUTE_BY_CATEGORY = {
  EMPLOYMENT: "INDIVIDUAL",
  SALARY: "INDIVIDUAL",
  SECURITY: "COMMON",
  PRIVACY: "COMMON",
  REGULATION: "COMMON",
  NDA: "COMMON",
};
const distributeTypeHint = ref("");

const selectedTemplateAuto = ref(false);        // 본문 서식 있음 → 문서 자동 생성
const selectedTemplateUsesMaster = ref(false);  // 근로조건 마스터 연동 항목 있음
const MASTER_KEYS = ["contract_start", "monthly_total", "base_pay", "fixed_ot_hours", "job"];

function handleTemplateChange(templateId) {
  const t = contractStore.templates.find((x) => x.id === templateId);
  selectedTemplateAuto.value = !!t?.has_body;
  selectedTemplateUsesMaster.value = !!(t?.term_fields || []).some((f) => MASTER_KEYS.includes(f.key));
  if (selectedTemplateAuto.value) {
    // 자동 생성은 항상 직원별 문서
    createForm.value.distribute_type = "INDIVIDUAL";
    uploadFile.value = null;
    distributeTypeHint.value = "";
    selectedTemplateFile.value = "";
    return;
  }
  const def = t && DEFAULT_DISTRIBUTE_BY_CATEGORY[t.category];
  if (def) {
    createForm.value.distribute_type = def;
    if (def === "INDIVIDUAL") uploadFile.value = null;
    distributeTypeHint.value = `${t.category_display}는 보통 "${def === "COMMON" ? "공통" : "개별"}"로 배포합니다. 예외면 바꿔 주세요.`;
  } else {
    distributeTypeHint.value = "";
  }
  selectedTemplateFile.value = t?.template_file || "";
}
const selectedTemplateFile = ref("");

const router = useRouter();
const contractStore = useContractStore();

// --- 공통 ---
const activeTab = ref("contracts");

// --- 계약서 목록 ---
const filters = ref({ status: "", template__category: "" });
const createDialogVisible = ref(false);
const creating = ref(false);
const createForm = ref({
  template: null,
  title: "",
  distribute_type: "COMMON",
  deadline: "",
  contract_start_date: "",
  reminder_days: [7, 3, 1],
  target_type: "ALL",
  target_all_employees: true,
  target_departments: [],
  employee_ids: [],
  memo: "",
});
const uploadFile = ref(null);

// --- 대상 선택 ---
const departments = ref([]);
const deptLoading = ref(false);
const employeeList = ref([]);
const empLoading = ref(false);
const empDeptFilter = ref(null);
const empSearchKeyword = ref("");
const selectedEmployees = ref([]);
const empTableRef = ref(null);
let empSearchTimer = null;

// --- 양식 관리 ---
const templatesLoading = ref(false);
const templateDialogVisible = ref(false);
const savingTemplate = ref(false);
const editingTemplate = ref(null);
// 배포된 계약서가 있는 양식은 내용 잠김 (서버 locked 플래그) — 이름·설명·사용여부만 수정 가능
const isLockedTemplate = computed(() => !!editingTemplate.value?.locked);

async function toggleTemplateActive(template) {
  const next = !template.is_active;
  try {
    await contractStore.updateTemplateJSON(template.id, { is_active: next });
    ElMessage.success(next ? "양식을 다시 활성으로 바꿨습니다." : "양식을 비활성으로 바꿨습니다. 계약서 생성 창에서 더 이상 보이지 않습니다.");
    fetchTemplates();
  } catch (e) {
    ElMessage.error("변경 실패: " + (e.response?.data?.locked || e.response?.data?.detail || e.message));
  }
}

async function handleNewVersion(template) {
  try {
    await ElMessageBox.confirm(
      `「${template.name}」 v${template.version || 1}의 내용을 복사한 새 버전 v${(template.version || 1) + 1}을 만들고, 이 버전은 비활성으로 바꿉니다. 이미 배포된 계약서는 v${template.version || 1} 그대로 유지됩니다. 계속할까요?`,
      "새 버전 만들기", { type: "warning", confirmButtonText: "새 버전 만들기", cancelButtonText: "취소" }
    );
  } catch { return; }
  try {
    const created = await contractStore.newTemplateVersion(template.id);
    ElMessage.success(`v${created.version} 양식이 만들어졌습니다. 내용을 수정한 뒤 저장하세요.`);
    templateDialogVisible.value = false;
    await fetchTemplates();
    openTemplateDialog(created);
  } catch (e) {
    ElMessage.error("새 버전 생성 실패: " + (e.response?.data?.detail || e.message));
  }
}
// 분류별 기본값 — 인사담당이 처음 양식을 만들 때 빈 칸에서 시작하지 않도록
const CATEGORY_PRESETS = {
  EMPLOYMENT: { name: "근로계약서", items: ["위 근로계약 내용을 확인하였으며 이에 동의합니다.", "제4조 근로시간(09~18시, 휴게 12~13시) 및 추가근무 합의 원칙에 동의합니다.", "본 근로계약서를 근로기준법 제17조에 의거하여 서면(전자문서)으로 교부받았습니다."], full: true, seconds: 60 },
  SALARY:     { name: "연봉계약서", items: ["위 연봉계약 내용을 확인하였으며 이에 동의합니다."], full: true, seconds: 60 },
  SECURITY:   { name: "보안서약서", items: ["회사의 보안 규정을 숙지하였으며 이를 준수할 것을 서약합니다."], full: true, seconds: 30 },
  PRIVACY:    { name: "개인정보 수집·이용 동의서", items: ["개인정보 수집·이용에 동의합니다.", "개인정보 제3자 제공에 동의합니다."], full: true, seconds: 30 },
  REGULATION: { name: "사내규정 동의서", items: ["취업규칙 및 사내규정을 확인하였으며 이를 준수하는 데 동의합니다."], full: true, seconds: 30 },
  NDA:        { name: "영업비밀보호서약서", items: ["서명에 앞서 위 서약사항을 세심히 읽어보았으며, 각 조항을 준수할 것을 서약합니다."], full: true, seconds: 60 },
  OTHER:      { name: "", items: ["위 내용을 확인하였으며 이에 동의합니다."], full: true, seconds: 30 },
};

const templateForm = ref(createEmptyTemplateForm());
const templateFile = ref(null); // 양식 파일(원본 서식) — 선택 시에만 multipart로 전송

function createEmptyTemplateForm(category = "EMPLOYMENT") {
  const p = CATEGORY_PRESETS[category] || CATEGORY_PRESETS.OTHER;
  return {
    name: p.name,
    category,
    description: "",
    version: "1",
    consent_items: [...p.items],
    consent_items_required: p.items.map(() => true),
    require_full_read: p.full,
    minimum_read_seconds: p.seconds,
    require_signature: true,
    term_fields: [],
    body_html: "",
    is_active: true,
  };
}

// 서버 term_fields → 폼 행 (select 옵션은 텍스트로 편집)
function termFieldsToForm(fields) {
  return (fields || []).map((f) => ({
    key: f.key, label: f.label, type: f.type || "text", required: !!f.required,
    default: f.default || "", unit: f.unit || "", help: f.help || "",
    optionsText: (f.options || []).join(", "),
  }));
}
function termFieldsFromForm(rows) {
  return (rows || []).filter((r) => (r.label || "").trim()).map((r) => ({
    key: r.key, label: r.label.trim(), type: r.type, required: !!r.required,
    default: r.default || "", unit: r.unit || "", help: r.help || "",
    options: r.type === "select" ? (r.optionsText || "").split(",").map((x) => x.trim()).filter(Boolean) : [],
  }));
}
const CATEGORY_LABELS = { EMPLOYMENT: "근로계약서", SALARY: "연봉계약서", SECURITY: "보안서약서", PRIVACY: "개인정보동의서", REGULATION: "사내규정동의서", NDA: "비밀유지서약서", OTHER: "기타" };
// 화면에 보이는 분류 — 회사는 현재 근로계약서·영업비밀보호서약서 두 가지만 씀 (2026-09-12 사이먼).
// 다른 종류가 필요해지면 여기 키만 추가하면 됨 (서버 모델·프리셋·배포유형 기본값은 전 분류 그대로 남아 있음).
const VISIBLE_CATEGORIES = ["EMPLOYMENT", "NDA"];
const CATEGORY_OPTIONS = VISIBLE_CATEGORIES.map((v) => ({ value: v, label: CATEGORY_LABELS[v] }));
const categoryLabel = computed(() => CATEGORY_LABELS[templateForm.value.category] || "");
const hasStandardBody = computed(() => ["EMPLOYMENT", "NDA"].includes(templateForm.value.category));
const termKeysText = computed(() => termFieldsFromForm(templateForm.value.term_fields).map((f) => `${f.label}=${f.key || "(저장 시 생성)"}`).join(", "));
const previewingBody = ref(false);

async function loadStandardBody() {
  try {
    const d = await contractStore.fetchTemplateDefaults(templateForm.value.category);
    if (!d.body_html) { ElMessage.info("이 분류에는 내장 표준 서식이 없습니다."); return; }
    if (templateForm.value.body_html && templateForm.value.body_html !== d.body_html) {
      await ElMessageBox.confirm("지금 입력된 서식을 표준 서식으로 바꿉니다. 계속할까요?", "표준 서식 불러오기", { type: "warning" });
    }
    templateForm.value.body_html = d.body_html;
    if (templateForm.value.term_fields.length === 0 && d.term_fields?.length) templateForm.value.term_fields = termFieldsToForm(d.term_fields);
  } catch (e) {
    if (e !== "cancel") ElMessage.error("표준 서식을 불러오지 못했습니다: " + (e.response?.data?.error || e.message));
  }
}

async function previewBody() {
  previewingBody.value = true;
  try {
    const blob = await contractStore.previewTemplateBody({ body_html: templateForm.value.body_html, term_fields: termFieldsFromForm(templateForm.value.term_fields) });
    window.open(URL.createObjectURL(blob), "_blank");
  } catch (e) {
    let msg = e.message;
    try { msg = JSON.parse(await e.response?.data?.text?.())?.error || msg; } catch (_) { /* 무시 */ }
    ElMessage.error("미리보기 실패: " + msg);
  } finally {
    previewingBody.value = false;
  }
}

// 신규 등록 중 분류를 바꾸면 양식명·동의문구·열람요건을 그 분류 기본값으로 다시 채움
// (수정 모드에서는 기존 값을 보존 — 분류만 바뀜)
async function applyCategoryPreset(category) {
  if (editingTemplate.value) return;
  const keep = { description: templateForm.value.description, version: templateForm.value.version, is_active: templateForm.value.is_active };
  templateForm.value = { ...createEmptyTemplateForm(category), ...keep };
  try {
    const d = await contractStore.fetchTemplateDefaults(category);
    templateForm.value.term_fields = termFieldsToForm(d.term_fields || []);
  } catch (e) { /* 기본 항목 없이 진행 */ }
}

const previewItems = computed(() =>
  (templateForm.value.consent_items || [])
    .map((label, idx) => ({ label: (label || "").trim(), required: templateForm.value.consent_items_required[idx] ?? true }))
    .filter((x) => x.label)
);

// --- 라이프사이클 ---
onMounted(() => {
  fetchContracts();
  fetchTemplates();
});

function handleTabChange(tab) {
  if (tab === "templates") {
    fetchTemplates();
  }
}

// ===== 계약서 관련 =====
async function fetchContracts() {
  const params = {};
  if (filters.value.status) params.status = filters.value.status;
  if (filters.value.template__category) params.template__category = filters.value.template__category;
  await contractStore.fetchContracts(params);
}

function resetFilters() {
  filters.value = { status: "", template__category: "" };
  fetchContracts();
}

function goDetail(row) {
  router.push(`/contract/${row.id}`);
}

function statusTagType(status) {
  const map = {
    DRAFT: "info",
    DISTRIBUTED: "warning",
    IN_PROGRESS: "",
    COMPLETED: "success",
    EXPIRED: "danger",
    CANCELLED: "info",
  };
  return map[status] || "";
}

function formatDate(d) {
  if (!d) return "";
  return d.substring(0, 10);
}

async function openCreateDialog() {
  distributeTypeHint.value = "";
  selectedTemplateFile.value = "";
  selectedTemplateAuto.value = false;
  selectedTemplateUsesMaster.value = false;
  createForm.value = {
    template: null,
    title: "",
    distribute_type: "COMMON",
    deadline: "",
    contract_start_date: "",
    reminder_days: [7, 3, 1],
    target_type: "ALL",
    target_all_employees: true,
    target_departments: [],
    employee_ids: [],
    memo: "",
  };
  uploadFile.value = null;
  selectedEmployees.value = [];
  empDeptFilter.value = null;
  empSearchKeyword.value = "";
  employeeList.value = [];
  createDialogVisible.value = true;

  // 부서 목록 미리 로드
  if (departments.value.length === 0) {
    deptLoading.value = true;
    try {
      departments.value = await contractStore.fetchDepartments();
    } catch {
      // ignore
    } finally {
      deptLoading.value = false;
    }
  }
}

function handleFileChange(file) {
  uploadFile.value = file.raw;
}

function handleTargetTypeChange(type) {
  createForm.value.target_all_employees = type === "ALL";
  createForm.value.target_departments = [];
  createForm.value.employee_ids = [];
  selectedEmployees.value = [];
  if (type === "INDIVIDUAL") {
    searchEmployees();
  }
}

async function searchEmployees() {
  empLoading.value = true;
  try {
    const params = {};
    if (empDeptFilter.value) params.department = empDeptFilter.value;
    if (empSearchKeyword.value) params.search = empSearchKeyword.value;
    employeeList.value = await contractStore.fetchEmployeesList(params);
  } catch {
    employeeList.value = [];
  } finally {
    empLoading.value = false;
  }
}

function debounceSearchEmployees() {
  clearTimeout(empSearchTimer);
  empSearchTimer = setTimeout(() => {
    searchEmployees();
  }, 300);
}

function handleEmployeeSelection(rows) {
  selectedEmployees.value = rows;
  createForm.value.employee_ids = rows.map((r) => r.id);
}

async function submitCreate() {
  if (selectedTemplateUsesMaster.value && !createForm.value.contract_start_date) {
    ElMessage.warning("계약 시작일(근로조건 기준일)을 입력하세요.");
    return;
  }
  if (!createForm.value.template || !createForm.value.title || !createForm.value.deadline) {
    ElMessage.warning("양식, 계약서명, 기한을 입력해주세요.");
    return;
  }
  if (createForm.value.distribute_type === "COMMON" && !uploadFile.value) {
    ElMessage.warning("공통 배포 유형은 계약서 파일을 선택해주세요.");
    return;
  }
  // 대상 검증
  if (createForm.value.target_type === "DEPARTMENT" && createForm.value.target_departments.length === 0) {
    ElMessage.warning("배포 대상 부서를 선택해주세요.");
    return;
  }
  if (createForm.value.target_type === "INDIVIDUAL" && createForm.value.employee_ids.length === 0) {
    ElMessage.warning("배포 대상 직원을 선택해주세요.");
    return;
  }

  creating.value = true;
  try {
    const formData = new FormData();
    formData.append("template", createForm.value.template);
    formData.append("title", createForm.value.title);
    formData.append("distribute_type", createForm.value.distribute_type);
    if (uploadFile.value) formData.append("document_file", uploadFile.value);
    formData.append("deadline", createForm.value.deadline);
    if (createForm.value.contract_start_date) formData.append("contract_start_date", createForm.value.contract_start_date);
    formData.append("reminder_days", JSON.stringify(createForm.value.reminder_days));
    formData.append("target_all_employees", createForm.value.target_all_employees);

    // 부서별 대상
    if (createForm.value.target_type === "DEPARTMENT") {
      createForm.value.target_departments.forEach((deptId) => {
        formData.append("target_departments", deptId);
      });
    }
    // 개별 대상
    if (createForm.value.target_type === "INDIVIDUAL") {
      formData.append("employee_ids", JSON.stringify(createForm.value.employee_ids));
    }

    if (createForm.value.memo) formData.append("memo", createForm.value.memo);

    await contractStore.createContract(formData);
    ElMessage.success("계약서가 생성되었습니다.");
    createDialogVisible.value = false;
    fetchContracts();
  } catch (e) {
    ElMessage.error("생성 실패: " + (e.response?.data?.error || e.message));
  } finally {
    creating.value = false;
  }
}

// ===== 양식 관련 =====
async function fetchTemplates() {
  templatesLoading.value = true;
  try {
    await contractStore.fetchTemplates();
  } finally {
    templatesLoading.value = false;
  }
}

function openTemplateDialog(template = null) {
  editingTemplate.value = template;
  templateFile.value = null;

  if (template) {
    // 수정 모드: 기존 consent_items JSON → 배열 분리
    const items = (template.consent_items || []).map((c) =>
      typeof c === "string" ? c : c.label || ""
    );
    const required = (template.consent_items || []).map((c) =>
      typeof c === "object" ? c.required !== false : true
    );
    templateForm.value = {
      name: template.name,
      category: template.category,
      description: template.description || "",
      version: template.version || "1",
      consent_items: items.length > 0 ? items : [""],
      consent_items_required: required.length > 0 ? required : [true],
      require_full_read: template.require_full_read ?? true,
      minimum_read_seconds: template.minimum_read_seconds ?? 30,
      require_signature: template.require_signature ?? true,
      term_fields: termFieldsToForm(template.term_fields),
      body_html: template.body_html || "",
      is_active: template.is_active ?? true,
    };
  } else {
    templateForm.value = createEmptyTemplateForm();
    contractStore.fetchTemplateDefaults(templateForm.value.category)
      .then((d) => { templateForm.value.term_fields = termFieldsToForm(d.term_fields || []); })
      .catch(() => {});
  }
  templateDialogVisible.value = true;
}

function addConsentItem() {
  templateForm.value.consent_items.push("");
  templateForm.value.consent_items_required.push(true);
}

function removeConsentItem(idx) {
  templateForm.value.consent_items.splice(idx, 1);
  templateForm.value.consent_items_required.splice(idx, 1);
}

async function submitTemplate() {
  if (!templateForm.value.name || !templateForm.value.category) {
    ElMessage.warning("양식명과 분류를 입력해주세요.");
    return;
  }

  // consent_items → JSON 배열로 변환
  const consentItems = templateForm.value.consent_items
    .filter((item) => item.trim())
    .map((item, idx) => ({
      label: item.trim(),
      required: templateForm.value.consent_items_required[idx] ?? true,
    }));

  savingTemplate.value = true;
  try {
    const payload = {
      name: templateForm.value.name,
      category: templateForm.value.category,
      description: templateForm.value.description,
      consent_items: consentItems,
      require_full_read: templateForm.value.require_full_read,
      minimum_read_seconds: templateForm.value.minimum_read_seconds,
      require_signature: templateForm.value.require_signature,
      term_fields: termFieldsFromForm(templateForm.value.term_fields),
      body_html: templateForm.value.body_html || "",
      is_active: templateForm.value.is_active,
    };

    // 양식 파일이 선택되면 multipart, 아니면 JSON (기존 파일은 유지됨)
    let body = payload;
    if (templateFile.value) {
      body = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        body.append(k, k === "consent_items" || k === "term_fields" ? JSON.stringify(v) : String(v));
      });
      body.append("template_file", templateFile.value);
    }

    if (editingTemplate.value) {
      await (templateFile.value
        ? contractStore.updateTemplate(editingTemplate.value.id, body)
        : contractStore.updateTemplateJSON(editingTemplate.value.id, body));
      ElMessage.success("양식이 수정되었습니다.");
    } else {
      await (templateFile.value
        ? contractStore.createTemplate(body)
        : contractStore.createTemplateJSON(body));
      ElMessage.success("양식이 등록되었습니다.");
    }

    templateDialogVisible.value = false;
    fetchTemplates();
  } catch (e) {
    ElMessage.error("저장 실패: " + (e.response?.data?.locked || e.response?.data?.detail || e.message));
  } finally {
    savingTemplate.value = false;
  }
}

async function handleDeleteTemplate(template) {
  try {
    await ElMessageBox.confirm(
      `"${template.name}" 양식을 삭제하시겠습니까?`,
      "양식 삭제",
      { type: "warning" }
    );
    await contractStore.deleteTemplate(template.id);
    ElMessage.success("양식이 삭제되었습니다.");
    fetchTemplates();
  } catch {
    // 취소
  }
}
</script>

<style scoped>
.flow-card { margin-bottom: var(--sw-space-4); }
.tpl-section { margin: var(--sw-space-4) 0 var(--sw-space-2); padding-bottom: var(--sw-space-1); border-bottom: 1px solid var(--el-border-color-lighter); font-size: var(--sw-font-md); color: var(--sw-primary); }
.tpl-section:first-child { margin-top: 0; }
.form-inline-hint { margin-left: var(--sw-space-2); color: var(--el-text-color-secondary); font-size: var(--sw-font-sm); }
.consent-preview { width: 100%; padding: var(--sw-space-3); border: 1px dashed var(--el-border-color); border-radius: 4px; background: var(--el-fill-color-lighter); }
.consent-preview-title { font-weight: 600; margin-bottom: var(--sw-space-2); font-size: var(--sw-font-md); }
.consent-preview-row { display: flex; align-items: center; margin-bottom: var(--sw-space-1); }
.consent-preview-actions { margin-top: var(--sw-space-2); }
.term-row { display: flex; align-items: center; gap: var(--sw-space-2); margin-bottom: var(--sw-space-2); flex-wrap: wrap; }
.form-hint { font-size: var(--sw-font-xs); color: var(--el-text-color-secondary); line-height: 1.4; width: 100%; }
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.tab-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}
.filter-card {
  margin-bottom: 16px;
}
.consent-items-area {
  width: 100%;
}
.consent-item-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
</style>
